'use client';

import React, {
  createContext, useContext, useState, useCallback,
} from 'react';
import toast from 'react-hot-toast';
import { fetchJobs, fetchUserJobs } from '@/app/(dashboard)/jobs/functions';
import * as Sentry from '@sentry/react';

const JobsContext = createContext();

export const useJobs = () => {
  const context = useContext(JobsContext);
  if (context === undefined) {
    throw new Error('useJobs must be used within a JobsProvider');
  }
  return context;
};

export function JobsProvider({ children }) {
  const [jobs, setJobs] = useState({}); // Dictionary with job.id as key
  const [jobStages, setJobStages] = useState({
    applied: [],
    got_interview: [],
    got_offer: [],
    none: [],
    rejected: [],
  }); // Dictionary of job stages with arrays of job IDs
  const [bookmarkedJobIds, setBookmarkedJobIds] = useState([]); // Array of bookmarked job IDs
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const updateJobBookmarkStatus = useCallback((jobId, isBookmarked) => {
    setBookmarkedJobIds((prevIds) => {
      if (isBookmarked) {
        return prevIds.includes(jobId) ? prevIds : [...prevIds, jobId];
      }
      return prevIds.filter((id) => id !== jobId);
    });
  }, []);

  const updateJobStage = useCallback((jobId, newStage, oldStage = null) => {
    setJobStages((prevStages) => {
      const updatedStages = { ...prevStages };

      // Remove from old stage if specified
      if (oldStage && updatedStages[oldStage]) {
        updatedStages[oldStage] = updatedStages[oldStage].filter((id) => id !== jobId);
      } else {
        // If no old stage specified, remove from all stages
        Object.keys(updatedStages).forEach((stage) => {
          updatedStages[stage] = updatedStages[stage].filter((id) => id !== jobId);
        });
      }

      // Add to new stage
      if (updatedStages[newStage] && !updatedStages[newStage].includes(jobId)) {
        updatedStages[newStage] = [...updatedStages[newStage], jobId];
      }

      return updatedStages;
    });
  }, []);

  const getJobById = useCallback((jobId) => {
    return jobs[jobId] || null;
  }, [jobs]);

  const loadJobsData = useCallback(async (uid) => {
    if (!uid) {
      console.error('No uid provided to loadJobsData');
      return;
    }

    setLoading(true);
    try {
      // Fetch all jobs and user-specific data in parallel
      const [allJobs, userData] = await Promise.all([
        fetchJobs(),
        fetchUserJobs({ uid }),
      ]);

      // Create dictionary starting with all jobs from fetchJobs
      const jobsDict = {};
      allJobs.forEach((job) => {
        jobsDict[job.id] = job;
      });

      // Add any user-specific jobs that aren't already in the dictionary
      userData.allUserJobs.forEach((job) => {
        if (!jobsDict[job.id]) {
          jobsDict[job.id] = job;
        }
      });

      setJobs(jobsDict);
      setJobStages(userData.jobStages);
      setBookmarkedJobIds(userData.bookmarkedJobIds || []);
      setInitialized(true);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error fetching jobs data:', error);
      toast.error('Error loading jobs data');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUserData = useCallback(async (uid) => {
    if (!uid) return;

    try {
      const userData = await fetchUserJobs({ uid });
      setJobStages(userData.jobStages);
      setBookmarkedJobIds(userData.bookmarkedJobIds || []);

      // Add any new user jobs to the jobs dictionary
      userData.allUserJobs.forEach((job) => {
        setJobs((prevJobs) => ({
          ...prevJobs,
          [job.id]: job,
        }));
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error refreshing user data:', error);
    }
  }, []);

  const value = {
    // State
    jobs,
    jobStages,
    bookmarkedJobIds,
    loading,
    initialized,

    // Functions
    updateJobBookmarkStatus,
    updateJobStage,
    getJobById,
    loadJobsData,
    refreshUserData,

    // Computed values
    appliedJobs: jobStages.applied.map((id) => jobs[id]).filter(Boolean),
    bookmarkedJobs: bookmarkedJobIds.map((id) => jobs[id]).filter(Boolean),
    interviewJobs: jobStages.got_interview.map((id) => jobs[id]).filter(Boolean),
    offerJobs: jobStages.got_offer.map((id) => jobs[id]).filter(Boolean),
    rejectedJobs: jobStages.rejected.map((id) => jobs[id]).filter(Boolean),
  };

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>;
}
